import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useWallet } from '../context/WalletContext';
import { useContracts } from '../hooks/useContracts';
import { useNotify } from '../context/NotificationContext';
import { parseError } from '../utils/errors';
import { waitForReceiptWithRetry, receiptSucceeded } from '../utils/txWait';
import GlassCard from '../components/common/GlassCard';
import NeonButton from '../components/common/NeonButton';
import ConnectPrompt from '../components/common/ConnectPrompt';
import HexLoader from '../components/loader/HexLoader';

const Register = () => {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const { account } = useWallet();
  const notify = useNotify();
  const c = useContracts();

  const [refId, setRefId] = useState(sp.get('ref') || '');
  const [checking, setChecking] = useState(true);
  const [busy, setBusy] = useState(false);
  const [txLabel, setTxLabel] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!account) { setChecking(false); return; }
      try {
        const id = await c.read.store.addressToId(account);
        if (!mounted) return;
        if (BigInt(id) > 0n) {
          notify.info('Already Registered', 'Redirecting to dashboard.');
          nav('/');
          return;
        }
      } catch {}
      if (mounted) setChecking(false);
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  const doRegister = async () => {
    try {
      setBusy(true);
      setTxLabel('Registering');
      const rid = BigInt(refId || '0');
      const tx = await c.write.logic.register(rid);

      setTxLabel('Confirming Transaction');
      const signerProvider = c.write.logic.runner && c.write.logic.runner.provider;
      const readProvider   = c.read.logic.runner;
      const receipt = await waitForReceiptWithRetry(tx, [signerProvider, readProvider]);

      if (receipt) {
        if (!receiptSucceeded(receipt)) throw new Error('Register tx reverted on-chain.');
      } else {
        setTxLabel('Verifying On-Chain');
        await new Promise((r) => setTimeout(r, 3000));
        const idAfter = BigInt(await c.read.store.addressToId(account).catch(() => 0n));
        if (idAfter === 0n) throw new Error('Could not confirm the registration. Tx: ' + tx.hash);
      }

      notify.success('Registered', 'Welcome to Graphenex. Redirecting to Invest…');
      nav('/invest');
    } catch (e) {
      notify.error('Registration Failed', parseError(e));
    } finally {
      setBusy(false); setTxLabel('');
    }
  };

  if (!account) return <ConnectPrompt />;
  if (checking) return (
    <div className="empty-state">
      <span className="atom-spinner sp-lg" />
      <div className="empty-title mt-3">Checking Account</div>
    </div>
  );

  return (
    <div className="animate-fade-up">
      <div className="page-title">Register</div>
      <div className="page-subtitle">Create your Graphenex account. Investment happens on the next page.</div>

      <GlassCard title="New Account" className="register-card">
        <div className="input-label"><span>Referrer ID</span><span>Optional</span></div>
        <input type="number" placeholder="1000" value={refId} onChange={(e) => setRefId(e.target.value)} />
        <div className="mono" style={{ fontSize: 11, color: 'var(--grx-text-mute)', marginTop: 4 }}>
          Leave blank to use the default referrer. The referrer must be an active investor.
        </div>

        <div className="alert a-info mt-3">
          Registration is a free, Invest to activate your account.
        </div>

        <div className="mt-3">
          <NeonButton block size="lg" onClick={doRegister} loading={busy}>
            Register
          </NeonButton>
        </div>
      </GlassCard>

      {busy && <HexLoader label={txLabel || 'Processing'} sub="Please confirm the transaction in your wallet." />}
    </div>
  );
};

export default Register;
